/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiForm,
  EuiFormRow,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFieldText,
  EuiButton,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import type { Tag } from '../types'
import type { ReactElement } from 'react'

type ValidationErrorCode =
  | 'key_max_length'
  | 'value_max_length'
  | 'max_tags'
  | 'has_duplicates'
  | 'invalid_key'
  | 'invalid_value'

type ValidationErrorMessage = ReactElement<typeof FormattedMessage> | null

export type ValidationErrors = {
  [K in ValidationErrorCode]?: ValidationErrorMessage
}

export type Props = {
  currentTags: Tag[]
  setCurrentTags: (tags: Tag[]) => void
  validateTag: (currentTag: Tag, currentTags: Tag[]) => ValidationErrors
  onFocus: () => void
}

const AddTagsForm = ({ currentTags, setCurrentTags, validateTag, onFocus }: Props) => {
  const { formatMessage } = useIntl()

  const [currentTagKey, setCurrentTagKey] = useState('')
  const [currentTagValue, setCurrentTagValue] = useState('')
  const [errors, setErrors] = useState<ValidationErrors>({})

  const getTagValidationError = (): ValidationErrorMessage => {
    const [error = null] = Object.values(errors)
    return error
  }

  const error = getTagValidationError()

  const canAddTags = (): boolean =>
    error === null && currentTagKey.trim() !== '' && currentTagValue.trim() !== ''

  const currentTagKeyRef = useRef<HTMLInputElement>(null)
  const currentTagValueRef = useRef<HTMLInputElement>(null)

  const onAddTag = () => {
    setCurrentTags([{ key: currentTagKey, value: currentTagValue }, ...currentTags])
    setCurrentTagKey('')
    setCurrentTagValue('')

    currentTagKeyRef.current?.focus()
  }

  const onKeyDownOnKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      currentTagValueRef.current?.focus()
    }
  }

  const onKeyDownOnValue = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()

      if (canAddTags()) {
        onAddTag()
      }
    }
  }

  const onTagKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFieldText = e.target.value
    setCurrentTagKey(inputFieldText)
    setErrors(validateTag({ key: inputFieldText, value: currentTagValue }, currentTags))
  }

  const onTagValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFieldText = e.target.value
    setCurrentTagValue(inputFieldText)
    setErrors(validateTag({ value: inputFieldText, key: currentTagKey }, currentTags))
  }

  return (
    <EuiForm data-test-id='resource-tags.flyout.form'>
      <EuiFormRow
        isInvalid={!!error}
        error={<div style={{ maxWidth: '70%' }}>{error}</div>}
        fullWidth={true}
      >
        <EuiFlexGroup alignItems='center' gutterSize='m'>
          <EuiFlexItem grow={4}>
            <EuiFieldText
              inputRef={currentTagKeyRef}
              autoFocus={true}
              isInvalid={!!errors.key_max_length || !!errors.invalid_key}
              placeholder={formatMessage({
                id: 'resource-tags.key-placeholder',
                defaultMessage: 'Key',
              })}
              onChange={onTagKeyChange}
              onKeyDown={onKeyDownOnKey}
              value={currentTagKey}
              onFocus={onFocus}
            />
          </EuiFlexItem>

          <EuiFlexItem grow={4}>
            <EuiFieldText
              inputRef={currentTagValueRef}
              isInvalid={!!errors.value_max_length || !!errors.invalid_value}
              placeholder={formatMessage({
                id: 'resource-tags.value-placeholder',
                defaultMessage: 'Value',
              })}
              onChange={onTagValueChange}
              onKeyDown={onKeyDownOnValue}
              value={currentTagValue}
              onFocus={onFocus}
            />
          </EuiFlexItem>

          <EuiFlexItem grow={3} style={{ alignSelf: `right` }}>
            <EuiButton isDisabled={!canAddTags()} onClick={onAddTag}>
              <FormattedMessage id='resource-tags.flyout.add-tag-button' defaultMessage='Add' />
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFormRow>
    </EuiForm>
  )
}

export default AddTagsForm
